import React, { useState, useEffect } from "react";
import TokenIconManager from "./tokenIcons";
import PrelaunchTokenManager from "./PrelaunchTokenManager";
import TokensListAdder from "./addTokensToTokensList";

const AddCryptoAddress = () => {
  const [authKey, setAuthKey] = useState("");
  const [network, setNetwork] = useState({
    name: "",
    rpcUrl: "",
    factory: "",
    wrappedToken: "",
    primaryStable: "",
    secondaryStable: "",
  });
  const [tokens, setTokens] = useState([{ symbol: "", address: "" }]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [existingNetworks, setExistingNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");

  // 🧩 Fetch all existing networks
  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const res = await fetch("https://api.malidag.com/api/crypto/get-networks");
        const data = await res.json();
        if (Array.isArray(data)) setExistingNetworks(data);
      } catch (err) {
        console.error("❌ Error fetching networks:", err);
      }
    };
    fetchNetworks();
  }, []);

  // 🧩 When selecting an existing network
  const handleNetworkSelect = (e) => {
    const name = e.target.value;
    setSelectedNetwork(name);
    if (name === "") {
      setNetwork({
        name: "",
        rpcUrl: "",
        factory: "",
        wrappedToken: "",
        primaryStable: "",
        secondaryStable: "",
      });
    } else {
      const selected = existingNetworks.find((n) => n.name === name);
      if (selected) setNetwork(selected);
    }
  };

  // 🧩 Add new token field
  const addTokenField = () => {
    setTokens([...tokens, { symbol: "", address: "" }]);
  };

  // 🧩 Update token fields
  const updateToken = (index, field, value) => {
    const updated = [...tokens];
    updated[index][field] = value;
    setTokens(updated);
  };

  // 🧩 Submit to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch("https://api.malidag.com/api/crypto/add-crypto-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authKey,
          network,
          tokens: tokens.filter((t) => t.symbol && t.address),
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message || "✅ Successfully added network & tokens!");
      } else {
        setMessage(data.error || "❌ Failed to add crypto data.");
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Server error, please check console.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center items-center p-6">
      <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-2xl">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 text-center">
          🪙 Add Network & Tokens
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Admin Key */}
          <div>
            <label className="block text-sm font-medium">Admin Key</label>
            <input
              type="password"
              className="w-full border rounded-md p-2"
              value={authKey}
              onChange={(e) => setAuthKey(e.target.value)}
              placeholder="Enter your secret admin key"
              required
            />
          </div>

          {/* Select existing network */}
          {existingNetworks.length > 0 && (
            <div>
              <label className="block text-sm font-medium">Select Existing Network</label>
              <select
                className="w-full border rounded-md p-2"
                value={selectedNetwork}
                onChange={handleNetworkSelect}
              >
                <option value="">➕ Create New Network</option>
                {existingNetworks.map((n) => (
                  <option key={n._id} value={n.name}>
                    {n.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Network Fields */}
          <div className="border p-3 rounded-md bg-gray-50">
            <h3 className="font-medium text-gray-700 mb-2">Network Info</h3>

            {/* Network Name */}
            <div className="mb-2">
              <label className="block text-sm font-medium">Name</label>
              <input
                type="text"
                className="w-full border rounded-md p-2"
                value={network.name}
                onChange={(e) => setNetwork({ ...network, name: e.target.value })}
                placeholder="e.g. bsc"
                required
              />
            </div>

            {/* RPC URL */}
            <div className="mb-2">
              <label className="block text-sm font-medium">RPC URL</label>
              <input
                type="text"
                className="w-full border rounded-md p-2"
                value={network.rpcUrl}
                onChange={(e) => setNetwork({ ...network, rpcUrl: e.target.value })}
                placeholder="https://bsc-dataseed.binance.org/"
              />
            </div>

            {/* Factory */}
            <div className="mb-2">
              <label className="block text-sm font-medium">Factory Address</label>
              <input
                type="text"
                className="w-full border rounded-md p-2"
                value={network.factory}
                onChange={(e) => setNetwork({ ...network, factory: e.target.value })}
                placeholder="0xca143ce32fe78f1f7019d7d551a6402fc5350c73"
              />
            </div>

            {/* Wrapped Token */}
            <div className="mb-2">
              <label className="block text-sm font-medium">Wrapped Token</label>
              <input
                type="text"
                className="w-full border rounded-md p-2"
                value={network.wrappedToken}
                onChange={(e) => setNetwork({ ...network, wrappedToken: e.target.value })}
                placeholder="0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c"
              />
            </div>

            {/* Primary Stable */}
            <div className="mb-2">
              <label className="block text-sm font-medium">Primary Stable (e.g. USDT)</label>
              <input
                type="text"
                className="w-full border rounded-md p-2"
                value={network.primaryStable}
                onChange={(e) => setNetwork({ ...network, primaryStable: e.target.value })}
                placeholder="0x55d398326f99059fF775485246999027B3197955"
              />
            </div>

            {/* Secondary Stable */}
            <div className="mb-2">
              <label className="block text-sm font-medium">Secondary Stable (e.g. BUSD/USDC)</label>
              <input
                type="text"
                className="w-full border rounded-md p-2"
                value={network.secondaryStable}
                onChange={(e) => setNetwork({ ...network, secondaryStable: e.target.value })}
                placeholder="0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56"
              />
            </div>
          </div>

          {/* Token Fields */}
          <div className="border p-3 rounded-md bg-gray-50">
            <h3 className="font-medium text-gray-700 mb-2">Tokens</h3>

            {tokens.map((token, index) => (
              <div key={index} className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  className="border rounded-md p-2"
                  placeholder="Symbol (e.g. ETH)"
                  value={token.symbol}
                  onChange={(e) => updateToken(index, "symbol", e.target.value)}
                  required
                />
                <input
                  type="text"
                  className="border rounded-md p-2"
                  placeholder="Address (0x...)"
                  value={token.address}
                  onChange={(e) => updateToken(index, "address", e.target.value)}
                  required
                />
              </div>
            ))}

            <button
              type="button"
              onClick={addTokenField}
              className="text-blue-600 text-sm hover:underline"
            >
              + Add Another Token
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition"
          >
            {loading ? "Saving..." : "Save Network & Tokens"}
          </button>

          {/* Message */}
          {message && (
            <p
              className={`text-center mt-3 ${
                message.includes("✅") ? "text-green-600" : "text-red-500"
              }`}
            >
              {message}
            </p>
          )}
        </form>
        <TokenIconManager/>
        <PrelaunchTokenManager />
        <TokensListAdder/>
      </div>
    </div>
  );
};

export default AddCryptoAddress;
